import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Wrench, FileText, Laptop, Clock, CheckCircle, AlertTriangle } from "lucide-react";

const statusLabels = {
  appareil_depose: "Appareil déposé",
  en_attente_diagnostic: "En attente diagnostic",
  diagnostic_en_cours: "Diagnostic en cours",
  devis_en_cours: "Devis en cours",
  devis_envoye: "Devis envoyé",
  devis_accepte: "Devis accepté",
  devis_refuse: "Devis refusé",
  en_attente_pieces: "En attente de pièces",
  reparation_en_cours: "Réparation en cours",
  reparation_terminee: "Réparation terminée",
  pret_a_restituer: "Prêt à restituer",
  restitue: "Restitué",
  annule: "Annulé",
};

const statusColors = {
  appareil_depose: "bg-blue-100 text-blue-800",
  diagnostic_en_cours: "bg-yellow-100 text-yellow-800",
  en_attente_diagnostic: "bg-yellow-100 text-yellow-800",
  devis_en_cours: "bg-orange-100 text-orange-800",
  devis_envoye: "bg-purple-100 text-purple-800",
  devis_accepte: "bg-green-100 text-green-800",
  devis_refuse: "bg-red-100 text-red-800",
  en_attente_pieces: "bg-orange-100 text-orange-800",
  reparation_en_cours: "bg-cyan-100 text-cyan-800",
  reparation_terminee: "bg-teal-100 text-teal-800",
  pret_a_restituer: "bg-green-100 text-green-800",
  restitue: "bg-gray-100 text-gray-800",
  annule: "bg-red-100 text-red-800",
};

const ClientPortalDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await API.get("/portal/dashboard");
        setData(res.data);
      } catch (error) {
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="client-portal-dashboard">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Bonjour, {data.client?.prenom}
        </h1>
        <p className="text-muted-foreground">Bienvenue dans votre espace client Micro Multimédia</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <Wrench className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Dossiers en cours</p>
                <p className="text-2xl font-bold" data-testid="active-cases-count">{data.stats.active_cases}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100">
                <FileText className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Devis en attente</p>
                <p className="text-2xl font-bold" data-testid="pending-quotes-count">{data.stats.pending_quotes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                <Laptop className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Appareils enregistrés</p>
                <p className="text-2xl font-bold" data-testid="devices-count">{data.stats.total_devices}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Quotes */}
      {data.pending_quotes.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Devis en attente de votre réponse
            </CardTitle>
            <CardDescription>Vous avez des devis qui nécessitent votre validation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.pending_quotes.map((quote) => (
              <div key={quote.id} className="flex items-center justify-between p-4 rounded-lg bg-white border">
                <div>
                  <p className="font-medium">{quote.numero}</p>
                  <p className="text-sm text-muted-foreground">Dossier {quote.sav_case_numero}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg">{quote.total_ttc?.toFixed(2)}€</span>
                  <Button size="sm" onClick={() => navigate("/portal/quotes")} data-testid={`view-quote-${quote.id}`}>
                    Voir le devis
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active Cases */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Dossiers en cours
          </CardTitle>
          <CardDescription>Suivez l'avancement de vos réparations</CardDescription>
        </CardHeader>
        <CardContent>
          {data.active_cases.length > 0 ? (
            <div className="space-y-3">
              {data.active_cases.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-secondary/30 cursor-pointer transition-colors"
                  onClick={() => navigate(`/portal/sav/${c.id}`)}
                  data-testid={`case-card-${c.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Wrench className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{c.numero}</p>
                      <p className="text-sm text-muted-foreground">
                        {c.device?.marque} {c.device?.modele}
                      </p>
                    </div>
                  </div>
                  <Badge className={statusColors[c.statut] || "bg-gray-100 text-gray-800"}>
                    {statusLabels[c.statut] || c.statut}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <p className="font-medium">Aucun dossier en cours</p>
              <p className="text-sm text-muted-foreground">Tous vos appareils sont en bon état</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientPortalDashboard;
